import {
  BugReportTwoTone,
  LocalShippingTwoTone,
  MoreVertTwoTone,
} from '@mui/icons-material'
import {
  Avatar,
  Badge,
  Button,
  Chip,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
} from '@mui/material'
import { matchSorter } from 'match-sorter'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  useFilters,
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from 'react-table'
import JsonView from 'react18-json-view'
import 'react18-json-view/src/style.css'
// If dark mode is needed, import `dark.css`.
// import 'react18-json-view/src/dark.css'

import { PayloadPool } from '@/types/domain/payload'

import { DDMMYYYYHHMMSS, userLocales } from '../../api'
import { AM, AMItem, DlgPayloadIssues, HStack, KPage } from '../../components'
import { usePayloadPool } from '../../hooks'

const GermanDate = data =>
  new Date(data.value).toLocaleDateString(userLocales, DDMMYYYYHHMMSS)

const isValidJson = payload => {
  let result = false

  try {
    JSON.parse(
      payload
        .replaceAll("'", '"')
        .replaceAll('None', '"undefined"')
        .replaceAll('True', '"True"')
        .replaceAll('False', '"False"')
    )
    result = true
  } catch {
    result = false
  }

  return result
}

const tableColumns = [
  {
    accessor: 'device_id',
    filterAll: true,
    Header: 'Device ID',
    id: 'device_id',
  },
  {
    accessor: 'payload',
    filterAll: true,
    Header: 'Payload',
    id: 'payload',
  },
  {
    accessor: 'timestamp',
    Cell: GermanDate,
    filterAll: true,
    Header: 'letzte Aktivität',
    id: 'timestamp',
    width: 150,
  },
]

const parseJSON = (device_id, timestamp, payload) => {
  let parsedJSON

  const issues = []

  try {
    parsedJSON = JSON.parse(payload)
  } catch (error) {
    issues.push({ device_id, timestamp, payload, error })
  }

  return parsedJSON
}

export const Payloads = () => {
  const { device_id } = useParams()

  const payloadPool: PayloadPool = usePayloadPool()

  const [actionMenu, setActionMenu] = useState(null)
  const [showIssues, setShowIssues] = useState(false)
  const [payloadIssues, setPayloadIssues] = useState(0)

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    state: { pageIndex, pageSize },
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    pageCount,
    //setFilter,
  } = useTable(
    {
      columns: useMemo(() => tableColumns, []),
      data: useMemo(() => {
        const singlePayload = data => {
          if (!data) return null

          const { device_id, payload, timestamp } = data

          const json = parseJSON(
            device_id,
            timestamp,
            payload
              .replaceAll("'", '"')
              .replaceAll('None', '"undefined"')
              .replaceAll('True', '"True"')
              .replaceAll('False', '"False"')
          )

          return {
            device_id,
            payload: <JsonView collapsed src={json} />,
            timestamp,
            id: data.id,
          }
        }

        return payloadPool.map(singlePayload).filter(Boolean)
      }, [payloadPool]),
      defaultColumn: useMemo(() => ({ Filter: () => null }), []),
      filterTypes: useMemo(
        () => ({
          text: (rows, id, filterValue) =>
            matchSorter(rows, filterValue, { keys: [id] }),
        }),
        []
      ),
      initialState: useMemo(
        () => ({
          sortBy: [{ id: 'timestamp', desc: true }],
          filters: [{ id: 'device_id', value: device_id || '' }],
          pageSize: 50,
        }),
        [device_id]
      ),
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  )

  const onFilter = useCallback(() => {
    console.log('onFilter')
  }, [])

  useEffect(() => {
    if (onFilter) onFilter()
  }, [pageIndex, pageSize, onFilter])

  useEffect(() => {
    if (payloadPool?.filter(item => !isValidJson(item.payload)).length > 0) {
      setPayloadIssues(
        payloadPool?.filter(item => !isValidJson(item.payload)).length
      )
    }
  }, [payloadPool])

  return (
    <>
      {/* action menu */}
      <AM
        anchorEl={actionMenu}
        caption={'Payloads'}
        onClose={() => setActionMenu(null)}
        open={Boolean(actionMenu)}
        historyUrlTarget={'sensors'}
      >
        <AMItem
          caption={'Ungültige Payloads'}
          icon={<BugReportTwoTone />}
          onClick={() => setShowIssues(true)}
        />
      </AM>
      {/* action menu end */}

      <DlgPayloadIssues
        open={showIssues}
        onClose={() => setShowIssues(false)}
        data={payloadPool}
        issues={payloadIssues}
      />

      <KPage
        action={
          <>
            <Tooltip title={'Ungültige Payloads'}>
              <IconButton onClick={() => setShowIssues(true)}>
                <Badge
                  badgeContent={payloadIssues}
                  color={payloadIssues === 0 ? 'success' : 'error'}
                >
                  <BugReportTwoTone />
                </Badge>
              </IconButton>
            </Tooltip>

            <IconButton onClick={event => setActionMenu(event.currentTarget)}>
              <MoreVertTwoTone />
            </IconButton>
          </>
        }
        avatar={
          <Avatar>
            <LocalShippingTwoTone />
          </Avatar>
        }
        title={
          <>
            Payloads
            <Chip label={payloadPool.length} sx={{ ml: 1 }} />
          </>
        }
      >
        <TableContainer>
          <Table sx={{ width: 1 }} {...getTableProps()}>
            <TableHead>
              {headerGroups.map(headerGroup => (
                <TableRow
                  key={headerGroup.id}
                  {...headerGroup.getHeaderGroupProps()}
                >
                  {headerGroup.headers.map(column => (
                    <TableCell
                      key={column.id}
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                    >
                      {column.render('Header')}
                      <TableSortLabel
                        active={column.isSorted}
                        direction={column.isSortedDesc ? 'desc' : 'asc'}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>

            <TableBody {...getTableBodyProps()}>
              {page.map(row => {
                prepareRow(row)
                return (
                  <TableRow key={row.id} {...row.getRowProps()}>
                    {row.cells.map(cell => (
                      <TableCell
                        key={`${row.id}-${cell.column.id}`}
                        {...cell.getCellProps()}
                      >
                        {cell.render('Cell')}
                      </TableCell>
                    ))}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 2 }} />

        {/* Pagination Controls */}
        <HStack>
          <Button disabled={pageIndex === 0} onClick={() => previousPage()}>
            Zurück
          </Button>
          <Button
            disabled={pageIndex >= pageCount - 1}
            onClick={() => nextPage()}
          >
            Weiter
          </Button>

          <TablePagination
            component={'div'}
            count={pageCount}
            onPageChange={(event, newPage) => gotoPage(newPage)}
            onRowsPerPageChange={event =>
              setPageSize(Number(event.target.value))
            }
            page={pageIndex}
            rowsPerPage={Number(pageSize)}
            rowsPerPageOptions={[10, 15, 20, 25, 50, 100]}
          />
        </HStack>
      </KPage>
    </>
  )
}
